import {
    getWorkflowItemBySlug,
    OVERVIEW_SLUG,
} from "@/modules/engagement/workflow/workflowItems";
import { Paths } from "@/paths";

import { LINK_TARGET_SLUG, TILE_SPECS } from "./opsDashboard.types";
import type { OpsAlertRow } from "./opsDashboard.types";

/**
 * Where a flagged row's link goes.
 *
 * Two inputs, per MPN-1840: the row says WHICH record (`subject_type` plus the ids it
 * carries), and the tile says WHICH PAGE of it — "link targets are campaign-scoped and
 * differ per table: Monitor, Schedule, Audience, or Ad Group. Each table ticket names its
 * own target." Tables whose ticket names none fall back to Monitor, which is the epic's
 * stated purpose.
 *
 * Contract and organization rows ignore the tile's target: those pages have no campaign
 * tabs to land on.
 *
 * NOT EVERY TAB EXISTS ON EVERY ENGAGEMENT. A CREATE engagement has only Team, Content and
 * Close — no Monitor, Schedule, Ad Groups or Audience — and 46 of 66 staging engagements on
 * prod are CREATE, so most of the Onboarding tab is affected. `EngagementDetailPage` does
 * not reject an unknown slug; it falls through to the overview, so the link would appear to
 * work while the URL claimed a tab the page was not showing. The slug is validated against
 * the engagement's own workflow definition and degrades to the overview when it does not
 * exist, so the address bar always matches what is rendered.
 *
 * Null when there is nothing to point at — the cell then renders as plain text rather
 * than as a link that goes nowhere.
 */
export function rowHref(row: OpsAlertRow, tileKey: string): string | null {
    const base = import.meta.env.BASE_URL;
    const target = TILE_SPECS[tileKey]?.linkTarget ?? "monitor";
    const wanted = LINK_TARGET_SLUG[target];

    // Asked of the workflow definition rather than hardcoded here, so a change to either
    // engagement type's tabs is followed automatically.
    const slug =
        row.engagement_type && getWorkflowItemBySlug(row.engagement_type, wanted)
            ? wanted
            : OVERVIEW_SLUG;

    switch (row.subject_type) {
        case "launch":
        case "engagement":
        case "ad_group":
            return row.engagement_id
                ? `${base}${Paths.ENGAGEMENT}/${row.engagement_id}/${slug}`
                : null;

        case "contract":
            return row.contract_id ? `${base}${Paths.CONTRACT_DETAIL}/${row.contract_id}` : null;

        case "organization":
            return row.organization_id
                ? `${base}${Paths.ORGANIZATION_DETAIL}/${row.organization_id}`
                : null;

        case "ingest_run": {
            // The Files page is keyed by ingest definition, which the row carries.
            const definitionId = row.payload.ingest_definition_id;
            return typeof definitionId === "number"
                ? `${base}configs/${definitionId}/files`
                : null;
        }

        default:
            return null;
    }
}
